import React, { useEffect, useState } from 'react';
import { Save, Loader2, CheckCircle2, Mail, Phone, MapPin, Linkedin, Twitter, Instagram } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const FIELDS = [
  { key: 'phone_primary', ar: 'الهاتف الرئيسي', en: 'Primary phone', Icon: Phone },
  { key: 'email', ar: 'البريد الإلكتروني', en: 'Email', Icon: Mail },
  { key: 'address_ar', ar: 'العنوان (عربي)', en: 'Address (Arabic)', Icon: MapPin, dir: 'rtl' },
  { key: 'address_en', ar: 'العنوان (إنجليزي)', en: 'Address (English)', Icon: MapPin, dir: 'ltr' },
  { key: 'linkedin', ar: 'LinkedIn', en: 'LinkedIn', Icon: Linkedin },
  { key: 'twitter', ar: 'Twitter / X', en: 'Twitter / X', Icon: Twitter },
  { key: 'instagram', ar: 'Instagram', en: 'Instagram', Icon: Instagram },
];

const AdminContact = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: null, msg: '' });

  useEffect(() => {
    adminApi.get('/settings/contact_info')
      .then(({ data }) => setData(data.value || {}))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setStatus({ type: null, msg: '' });
    try {
      await adminApi.put('/settings/contact_info', { value: data });
      setStatus({ type: 'success', msg: isAr ? 'تم الحفظ بنجاح' : 'Saved successfully' });
      setTimeout(() => setStatus({ type: null, msg: '' }), 2500);
    } catch (e) {
      setStatus({ type: 'error', msg: formatApiErrorDetail(e?.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const txt = isAr ? 'text-right' : 'text-left';
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <h1 className={`text-2xl md:text-3xl font-bold text-slate-900 dark:text-white ${txt}`}>{t.contact}</h1>

      <div className={`rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 space-y-4 ${txt}`}>
        {FIELDS.map(({ key, ar, en, Icon, dir }) => (
          <div key={key}>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{isAr ? ar : en}</label>
            <div className={`relative ${isAr ? 'flex-row-reverse' : ''}`}>
              <Icon className={`absolute top-3 ${isAr ? 'right-3' : 'left-3'} w-4 h-4 text-slate-400`} />
              <input
                type="text"
                dir={dir || 'ltr'}
                value={data[key] || ''}
                onChange={(e) => setData((p) => ({ ...p, [key]: e.target.value }))}
                data-testid={`contact-${key}`}
                className={`w-full ${isAr ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
        <button type="button" onClick={save} disabled={saving} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow-lg disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t.save}
        </button>
        {status.type === 'success' && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" /> {status.msg}
          </span>
        )}
        {status.type === 'error' && <span className="text-sm text-red-600">{status.msg}</span>}
      </div>
    </div>
  );
};

export default AdminContact;
